import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { AppHeader } from '../components/AppHeader';
import { Timeline } from '../components/Timeline';
import { useStoreSlice } from '../store/storeRegistry';

export default function TimelineTab() {
  const { selectedStoreId } = useStoreSlice();
  const qc = useQueryClient();
  const hasStore = Boolean(selectedStoreId);

  const configsQ = useQuery({
    queryKey: ['configs', selectedStoreId],
    queryFn: () => api.listConfigs(selectedStoreId),
    enabled: hasStore,
  });

  const conflictsQ = useQuery({
    queryKey: ['conflict-graph', selectedStoreId],
    queryFn: () => api.configsConflictGraph(selectedStoreId),
    staleTime: 60_000,
    enabled: hasStore,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['configs', selectedStoreId] });
    qc.invalidateQueries({ queryKey: ['conflict-graph', selectedStoreId] });
  };

  return (
    <div className="min-h-screen bg-ff-ivory relative">
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" aria-hidden="true" />

      <AppHeader active="timeline" />

      <main className="relative mx-auto max-w-[1240px] px-5 pb-24 pt-10 animate-rise sm:px-8 sm:pt-12">
        <div className="mb-10 border-b border-ff-line pb-10">
          <p className="ff-kicker mb-4">Campaign planning</p>
          <h1 className="ff-page-title text-[clamp(3.25rem,7vw,5.25rem)] leading-[0.92]">
            The calendar
            <span className="block text-ff-orange">of campaigns.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-ff-mute">
            Visualize when each config is active. Higher-priority bars win over
            lower-priority bars where they overlap. Click a bar to edit its
            active window.
          </p>
        </div>

        {!hasStore && (
          <div className="ff-card p-8 text-center text-ff-mute">
            Select a store to view its timeline.
          </div>
        )}

        {hasStore && (configsQ.isLoading || conflictsQ.isLoading) && (
          <p className="text-sm text-ff-mute">Loading…</p>
        )}

        {hasStore && (configsQ.isError || conflictsQ.isError) && (
          <p className="text-sm text-ff-danger">
            Couldn't load timeline data. Check that the backend is reachable.
          </p>
        )}

        {hasStore && !configsQ.isLoading && !conflictsQ.isLoading && !configsQ.isError && !conflictsQ.isError && (
          <Timeline
            configs={configsQ.data ?? []}
            conflictEdges={conflictsQ.data ?? []}
            storeId={selectedStoreId}
            onConfigSaved={invalidate}
          />
        )}
      </main>
    </div>
  );
}
